import type { ValidationRule } from "@/domains/buildings/types/buildingWidget";

export type FieldValidationResult =
  | { ok: true }
  | { ok: false; reason: "range" | "length" | "pattern" };

/**
 * Mirrors `data.validate_attribute_value` (PG function) for client-side
 * pre-flight checks on user-entered values.
 *
 * - `range`  — `validation_value` is "min,max" with empty entries treated as
 *              unbounded (e.g. "0,11000", ",500").
 * - `length` — same min,max format applied to `value.length`.
 * - `pattern` — anchored regex (starts with `^` or ends with `$`) is applied
 *               via `RegExp`. Otherwise the value's length is compared against
 *               the template length (matches the PG function's pattern branch).
 *
 * Enum / enum_multiselect aren't checked here — those are rendered as
 * dropdowns elsewhere and can't produce out-of-spec values.
 *
 * Empty values are treated as valid (the surrounding flow rejects them via a
 * separate "do-not-persist-empty" path).
 */
export function validateFieldValue(
  value: string,
  rules: ValidationRule[] | undefined
): FieldValidationResult {
  if (!rules || rules.length === 0 || value === "") return { ok: true };

  for (const rule of rules) {
    if (rule.validation_type === "range") {
      const n = Number(value);
      if (Number.isNaN(n)) return { ok: false, reason: "range" };
      const [min, max] = rule.validation_value.split(",");
      if (min && min !== "" && n < Number(min)) return { ok: false, reason: "range" };
      if (max && max !== "" && n > Number(max)) return { ok: false, reason: "range" };
    } else if (rule.validation_type === "length") {
      const [min, max] = rule.validation_value.split(",");
      if (min && min !== "" && value.length < Number(min)) {
        return { ok: false, reason: "length" };
      }
      if (max && max !== "" && value.length > Number(max)) {
        return { ok: false, reason: "length" };
      }
    } else if (rule.validation_type === "pattern") {
      const pat = rule.validation_value;
      try {
        if (pat.startsWith("^") || pat.endsWith("$")) {
          if (!new RegExp(pat).test(value)) return { ok: false, reason: "pattern" };
        } else if (value.length !== pat.length) {
          return { ok: false, reason: "pattern" };
        }
      } catch {
        // Malformed regex — fall through, treat as no validation.
      }
    }
  }
  return { ok: true };
}
